import { ControlType, Property, PropertyType } from "../../../base/types";
import { FC, useEffect } from "react";
import { IControlAttributes, ITableLookup } from "../../../base/models";

import { DesignerMode } from "../../types";
import { Registry } from "../../../base/register";
import { Typeahead } from "react-bootstrap-typeahead";
import clsx from "clsx";
import { getQueryFields } from "../../helpers";
import { useAuth } from "../../../../modules/auth";
import { useGetProperties } from "../../../base/hooks";
import { useLazyReadMultipleQuery } from "../../../services/data/tableDataApi";
import { usePageContext } from "../../page/context";
import { useState } from "react";

export const LookupFieldFilterComponent: FC<IControlAttributes> = ({
    control, className, children, props, designerMode, ...rest
}) => {
    const { properties, getPropertyValue } = useGetProperties({ bindings: control.bindings });
    const tableLookup: ITableLookup = properties[Property.TableLookup] || {};
    const label: string | undefined = properties[Property.Label];
    const tooltip: string | undefined = properties[Property.Tooltip];

    const [field, setField] = useState<string>()
    const [value, setValue] = useState<string>()
    const { pageView, getFieldFilter, setFieldFilter } = usePageContext()

    useEffect(() => {
        const field = getPropertyValue<string>(Property.Field)
        setField(field)
        // eslint-disable-next-line
    }, [properties])

    useEffect(() => {
        if (!field) return;
        const filter = getFieldFilter(field)
        setValue(filter);
        // eslint-disable-next-line
    }, [field, pageView?.clientFilters])

    const handleFilterChange = (value?: string) => {
        if (!field) return;
        setFieldFilter(field, value);
    }

    return (
        <div className={className}>
            {label &&
                <label
                    title={tooltip}
                    htmlFor={control.controlId}
                    className={clsx("form-label text-gray-600 fs-7")}>
                    {label}
                </label>}

            <SelectFieldValue
                id={control.controlId}
                tableLookup={tableLookup}
                value={value}
                readOnly={false}
                disabled={designerMode === DesignerMode.Draft}
                onChange={handleFilterChange}
            />
        </div>
    )
}

const SelectFieldValue = ({ id, tableLookup, value, className, readOnly, disabled, onChange }: {
    tableLookup: ITableLookup,
    id?: string,
    value?: string,
    readOnly: boolean,
    disabled: boolean,
    className?: string,
    onChange: (value?: string) => void,
}) => {
    const { user, siteId: portalId } = useAuth();
    const [readRecords, { data, isLoading }] = useLazyReadMultipleQuery()
    const [fields, setFields] = useState<{ id: string, label: string }[]>([])

    useEffect(() => {
        if (!user || !tableLookup)
            return;

        const { userId } = user;
        if (!userId || !portalId)
            return;

        if (!tableLookup.tableId || !tableLookup.idField || !tableLookup.displayField)
            return;

        var queryFields = getQueryFields([], [], tableLookup.filters || [])
        const fieldList = Array.from(new Set([...queryFields, tableLookup.idField, tableLookup.displayField]))
        const query = {
            userId, portalId,
            tableId: tableLookup.tableId,
            fieldList,
            filters: [{ group: 0, filters: tableLookup.filters || [] }],
        };

        readRecords(query)

        // eslint-disable-next-line
    }, [user, tableLookup])

    useEffect(() => {
        if (!data || !tableLookup.idField || !tableLookup.displayField) return;

        const idField = tableLookup.idField;
        const displayField = tableLookup.displayField;

        setFields(data.records.map((p) => ({ id: p[idField], label: p[displayField] })))

        // eslint-disable-next-line
    }, [data, tableLookup])

    const handleOnChange = (selected: Record<string, any>) => {
        const value = selected[0];

        if (value) {
            const newValue = value["id"];
            if (value !== newValue)
                onChange(newValue);
        } else
            onChange(undefined);
    }

    return (
        <Typeahead
            id={id}
            className={clsx(className, "custom-typeahead typeahead-sm")}
            labelKey={option => `${(option as any).label || ''}`}
            isLoading={isLoading}
            clearButton={true}
            disabled={disabled || readOnly}
            options={fields}
            filterBy={['label']}
            onChange={handleOnChange}
            selected={fields.filter(p => p.id === value) || ''}
        />
    )
}

Registry.addControl({
    id: "234cf922-83a6-4dd1-a719-6800e4a12223",
    name: "Lookup Field Filter",
    type: ControlType.LookupFieldFilter,
    properties: [
        { id: Property.Label, name: 'Label', type: PropertyType.String },
        { id: Property.Field, name: 'Field', type: PropertyType.String },
        { id: Property.TableLookup, name: 'Table Lookup', type: PropertyType.TableLookup },
        { id: Property.Tooltip, name: 'Tooltip', type: PropertyType.String }
    ],
    component: LookupFieldFilterComponent
})
